import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import CEUFormView from '../components/forms/CEUFormView';
import ceuService from '../services/unifiedCEUService';
import { getRequirements, getSubcategoryOptions } from '../config/ceuConfig';

const ceuSchema = yup.object({
  workshopName: yup.string().required('Workshop title is required'),
  instructor: yup.string().required('Instructor name is required'),
  provider: yup.string(),
  organization: yup.string(),
  date: yup.string().required('Date is required'),
  ceuHours: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('CEUs are required')
    .positive('Hours must be positive')
    .max(100, 'Hours cannot exceed 100'),
  description: yup.string(),
  certificateFile: yup
    .mixed()
    .nullable()
    .test('fileSize', 'File size must be less than 10MB', (value) => {
      if (!value) return true; // Optional field
      return value.size <= 10 * 1024 * 1024; // 10MB
    })
    .test('fileType', 'Only PDF, PNG, JPG, and JPEG files are allowed', (value) => {
      if (!value) return true; // Optional field
      return ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(value.type);
    }),
  category: yup.string().required('Category is required'),
  subCategory: yup.string(),
  processingStatus: yup.object({
    rid: yup.boolean(),
    bei: yup.boolean(),
  }),
});

export default function AddCEU() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // TODO: Get from user profile
  const userProfile = {
    certifyingBody: 'RID',
    hasBothCertifications: false,
  };

  const requirements = getRequirements(userProfile.certifyingBody);
  const availableCategories = requirements?.requirements 
    ? Object.keys(requirements.requirements).map(cat => ({
        value: cat,
        label: cat,
        description: '',
      }))
    : [
        { value: 'Professional Studies', label: 'Professional Studies', description: '' },
        { value: 'General Studies', label: 'General Studies', description: '' },
      ];

  const { control, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(ceuSchema),
    defaultValues: {
      workshopName: '',
      instructor: '',
      provider: '',
      organization: '',
      date: '',
      ceuHours: '',
      description: '',
      category: '',
      subCategory: '',
      processingStatus: {
        rid: false,
        bei: false,
      },
    },
  });

  const selectedCategory = watch('category');
  const availableSubcategories = selectedCategory 
    ? getSubcategoryOptions(userProfile.certifyingBody, selectedCategory)
    : [];

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const ceuData = {
        userId: user.userId,
        title: data.workshopName,
        instructor: data.instructor,
        provider: data.provider,
        organization: data.organization,
        date: data.date,
        ceuHours: parseFloat(data.ceuHours),
        description: data.description,
        category: data.category,
        subcategories: data.subCategory ? [data.subCategory] : [],
        ridReceived: data.processingStatus.rid,
        beiReceived: data.processingStatus.bei,
      };

      // First create the CEU record
      const result = await ceuService.createCEU(user.userId, ceuData);
      
      if (result.success) {
        let certificateUrl = null;
        
        // If there's a certificate file, upload it
        if (data.certificateFile) {
          const { s3PhotoService } = await import('../services/s3PhotoService');
          const uploadResult = await s3PhotoService.uploadCertificate(
            user.userId, 
            result.data.ceuId, 
            data.certificateFile
          );
          
          if (uploadResult.success) {
            certificateUrl = uploadResult.certificateUrl;
            
            // Update the CEU record with the certificate URL
            await ceuService.updateCEU(user.userId, result.data.ceuId, {
              certificateUrl: certificateUrl
            });
          } else {
            console.warn('Certificate upload failed:', uploadResult.error);
            // Don't fail the entire operation if certificate upload fails
          }
        }
        
        setSuccess('CEU added successfully!' + (certificateUrl ? ' Certificate uploaded.' : ''));
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(result.error || 'Failed to add CEU');
      }
    } catch (err) {
      setError(err.message || 'Failed to add CEU');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CEUFormView
      control={control}
      errors={errors}
      handleSubmit={handleSubmit(onSubmit)}
      isEditing={false}
      loading={loading}
      error={error}
      success={success}
      userProfile={userProfile}
      availableCategories={availableCategories}
      availableSubcategories={availableSubcategories}
      selectedCategory={selectedCategory}
      onBack={() => navigate('/dashboard')}
    />
  );
}
